import { useCallback, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useChatStore } from '../../chat/store/chatStore';
import { useBotStore } from '../../../shared/store/botStore';
// Fixed circular import: use direct path instead of barrel export
import { useAssistantHubStore } from '../../assistant-hub/store';
import { useFeedbackStore } from '../../chat/store/feedbackStore';
import { useCitationStore } from '../../../shared/store/citationStore';
import { useSharePointStore } from '../../cloud-storage/store/sharePointStore';
import { useRightSidebarStore } from '../../../shared/store/rightSidebarStore';
import { useNavigationStore } from '../../../shared/store/navigationStore';
import { authService } from '../services/GCPAuthService';
import { httpClient } from '@/shared/services/HttpClient';
import i18n from '@/i18n';

export function useAuth() {
  const {
    isAuthenticated,
    user,
    token,
    checkAuthExpiry,
    refreshCurrentUser,
    isLoggingOut,
    setIsLoggingOut,
  } = useAuthStore();

  // Check auth expiry on mount and periodically
  useEffect(() => {
    checkAuthExpiry();

    const interval = setInterval(() => {
      checkAuthExpiry();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [checkAuthExpiry]);

  // Refresh user data periodically to catch backend updates (e.g., role changes)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Refresh user data every 5 minutes
    const refreshInterval = setInterval(() => {
      refreshCurrentUser();
    }, 300000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, refreshCurrentUser]);

  const login = useCallback(() => {
    try {
      // GCP auth uses redirect, not async
      authService.initiateOIDCLogin();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : i18n.t('auth.error.verificationFailed'),
      };
    }
  }, []);

  const logout = useCallback(async () => {
    // Prevent concurrent logout attempts
    if (isLoggingOut) {
      // Return success to prevent the UI from showing errors
      return {
        success: true,
        errors: [],
      };
    }

    try {
      // Set logging out state FIRST
      setIsLoggingOut(true);

      // Clear auth state IMMEDIATELY to prevent UI flashing
      // This must happen before any async operations
      useAuthStore.getState().clearAuth();

      // Now perform cleanup operations in background
      const result = await authService.logout();

      // Centralized logout handler: clear ALL stores
      // Auth store is cleared by authService.logout() via TokenStorage
      useChatStore.getState().clearMessages();
      useBotStore.getState().clearBotSelection();
      useBotStore.getState().setDefaultBot(null);
      useAssistantHubStore.getState().clearState();
      useFeedbackStore.getState().clearState();

      // Clear additional stores that were missing
      useCitationStore.getState().reset?.();
      useSharePointStore.getState().reset?.();
      useRightSidebarStore.getState().reset?.();

      // Reset navigation store to default state
      const navigationStore = useNavigationStore.getState();
      navigationStore.setActiveSidebarSection?.('bots');
      navigationStore.setSidebarExpanded?.(true);
      navigationStore.setTransitioning?.(false);

      // Clear any pending requests in HttpClient
      // This helps prevent auth errors from pending requests after logout
      if (httpClient && typeof httpClient.cancelAllRequests === 'function') {
        httpClient.cancelAllRequests();
      }

      // Clear any persisted data in localStorage (except desired preferences)
      // Keep theme store and language preference
      const keysToKeep = ['theme-store', 'i18nextLng'];

      Object.keys(localStorage).forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      return result;
    } catch (error) {
      console.error('[useAuth] Logout error:', error);
      return {
        success: false,
        errors: [error instanceof Error ? error.message : 'Logout failed'],
      };
    } finally {
      // Reset logging out state immediately on completion
      // The delay was causing issues with component unmounting
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, setIsLoggingOut]);

  const refreshToken = useCallback(async () => {
    try {
      await authService.refreshToken();
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, []);

  return {
    isAuthenticated,
    user,
    token,
    login,
    logout,
    refreshToken,
    refreshCurrentUser,
    isLoggingOut,
  };
}
